// @section 1 Acquisitions / Pipeline — stage-flow view.
// Shows ALL workflow_stages columns (even empty), so the full ladder is
// visible. Click an account card → /acquisitions/:dealId.

import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { VERTICAL_LABELS, fmtUsd, fmtDays, type Deal } from '../../lib/acquisitions';
import { useAcquisitions } from '../../lib/AcquisitionsContext';
import { useIsMobile } from '../../lib/useIsMobile';
import { FeedScroll, FeedHeader, FeedPills, FeedEmpty, FeedCard } from '../../components/MobileFeed';

export default function PipelineView() {
  const acquisitions = useAcquisitions();
  const [stage, setStage] = useState<'all' | string>('all');
  const [vertical, setVertical] = useState<'all' | string>('cpa_tax_accounting');
  const [owner, setOwner] = useState<'all' | string>('all');
  const [q, setQ] = useState('');
  // @block 1.0 Phone fork: the swipe-sideways stage board becomes a single
  // urgency-sorted feed (stalled deals first, then most recently active).
  const isMobile = useIsMobile();

  const deals = acquisitions.deals;
  const stages = acquisitions.stages;

  const verticals = useMemo(() => {
    const set = new Set<string>();
    for (const d of deals) if (d.listing.vertical) set.add(d.listing.vertical);
    return Array.from(set).sort();
  }, [deals]);

  const owners = useMemo(() => {
    const set = new Set<string>();
    for (const d of deals) if (d.next_action_owner) set.add(d.next_action_owner);
    return Array.from(set).sort();
  }, [deals]);

  const filtered = useMemo(() => {
    return deals.filter((d) => {
      if (vertical !== 'all' && d.listing.vertical !== vertical) return false;
      if (stage !== 'all' && d.stage !== stage) return false;
      if (owner !== 'all' && d.next_action_owner !== owner) return false;
      if (q) {
        const needle = q.toLowerCase();
        const hay = [
          d.listing.business_name,
          d.listing.source_listing_id,
          d.listing.location_city,
          d.listing.location_state,
          d.broker?.company_name,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [deals, stage, vertical, owner, q]);

  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const d of filtered) counts[d.stage] = (counts[d.stage] ?? 0) + 1;
    return counts;
  }, [filtered]);

  // Stage display columns — show ALL non-terminal stages, plus any stage that
  // has deals (e.g. not_pursuing) even though it's marked is_terminal=false.
  // Append terminal stages (closed_won / closed_lost) at the end if they have deals.
  const columns = useMemo(() => {
    const seen = new Set<string>();
    const cols: { stageKey: string; displayName: string; isTerminal: boolean }[] = [];
    for (const s of stages) {
      if (s.is_terminal && (stageCounts[s.stage_key] ?? 0) === 0) continue;
      cols.push({ stageKey: s.stage_key, displayName: s.display_name, isTerminal: s.is_terminal });
      seen.add(s.stage_key);
    }
    // Stages on deals not in workflow_stages table.
    for (const sk of Object.keys(stageCounts)) {
      if (!seen.has(sk)) {
        cols.push({ stageKey: sk, displayName: sk.replace(/_/g, ' '), isTerminal: false });
      }
    }
    return cols;
  }, [stages, stageCounts]);

  if (isMobile) {
    return <AcqMobileFeed deals={deals} stageName={(k) => acquisitions.stageByKey(k)?.display_name ?? k.replace(/_/g, ' ')} />;
  }

  return (
    <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr', height: '100%', minHeight: 0 }}>
      <div style={{
        padding: '12px 26px 12px',
        borderBottom: '1px solid var(--t2-border)',
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <span className="sub" style={{ color: 'var(--tx-3)', fontSize: 12 }}>
          {filtered.length} of {deals.length} · as of {new Date(acquisitions.asOf).toLocaleDateString()}
        </span>
        <div className="pill-group">
          <button className={`pill${vertical === 'all' ? ' active' : ''}`} onClick={() => setVertical('all')}>
            All<span className="ct">{deals.length}</span>
          </button>
          {verticals.map((v) => (
            <button key={v} className={`pill${vertical === v ? ' active' : ''}`} onClick={() => setVertical(v)}>
              {VERTICAL_LABELS[v] ?? v}<span className="ct">{deals.filter(d => d.listing.vertical === v).length}</span>
            </button>
          ))}
        </div>
        <div className="pill-group">
          <button className={`pill${owner === 'all' ? ' active' : ''}`} onClick={() => setOwner('all')}>All owners</button>
          {owners.map((o) => (
            <button key={o} className={`pill${owner === o ? ' active' : ''}`} onClick={() => setOwner(o)}>{o}</button>
          ))}
        </div>
        <div className="search-box" style={{ marginLeft: 'auto' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
          </svg>
          <input placeholder="Search business, code, broker…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <div style={{ padding: '14px 26px 24px', overflow: 'auto', minHeight: 0 }}>
        <div className="pipeline-flow" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(220px, 1fr))` }}>
          {columns.map((c) => (
            <StageColumn
              key={c.stageKey}
              stageKey={c.stageKey}
              stageDisplay={c.displayName}
              count={stageCounts[c.stageKey] ?? 0}
              isActive={stage === c.stageKey}
              isTerminal={c.isTerminal}
              onActivate={() => setStage(stage === c.stageKey ? 'all' : c.stageKey)}
              deals={filtered.filter((d) => d.stage === c.stageKey)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function StageColumn({
  stageKey, stageDisplay, count, isActive, isTerminal, onActivate, deals,
}: {
  stageKey: string;
  stageDisplay: string;
  count: number;
  isActive: boolean;
  isTerminal: boolean;
  onActivate: () => void;
  deals: Deal[];
}) {
  const indicatorClass = stageIndicator(stageKey);
  return (
    <div className="stage-col" style={{ opacity: count === 0 ? 0.45 : 1 }}>
      <div
        className="stage-head"
        style={{ cursor: 'pointer', opacity: isActive ? 1 : 0.92 }}
        onClick={onActivate}
      >
        <div className="name">
          <span className={`indicator ${indicatorClass}`}></span>
          {stageDisplay}
          {isTerminal && <span style={{ fontSize: 9, color: 'var(--tx-4)', marginLeft: 6, letterSpacing: 0.5 }}>· terminal</span>}
        </div>
        <div className="count">{count}</div>
      </div>
      <div className="stage-col-body">
        {deals.length === 0 ? (
          <div style={{ fontSize: 10, color: 'var(--tx-5)', fontStyle: 'italic', padding: '8px 4px' }}>
            empty
          </div>
        ) : deals.map((d) => <DealCard key={d.deal_id} d={d} />)}
      </div>
    </div>
  );
}

function stageIndicator(stage: string): string {
  switch (stage) {
    case 'inquiry_sent':   return 'lead';
    case 'nda_pending':    return 'lead';
    case 'nda_signed':     return 'qualified';
    case 'cim_received':   return 'qualified';
    case 'broker_contact': return 'proposal';
    case 'meeting':        return 'proposal';
    case 'ioi_sent':       return 'negotiation';
    case 'loi_sent':       return 'negotiation';
    case 'under_loi':      return 'negotiation';
    case 'closed_won':     return 'won';
    default: return 'lead';
  }
}

function warmthClass(d: Deal): string {
  const open = (d.open_tasks ?? []).filter((t) => t.urgency === 'overdue').length;
  const last = d.summary?.last_touched_at ?? d.updated_at;
  const days = last ? Math.floor((Date.now() - new Date(last).getTime()) / 86400000) : 999;
  if (open > 0) return 'stall';
  if (days <= 3) return 'warm';
  if (days <= 7) return 'engaged';
  if (days <= 21) return '';
  return 'cold';
}

// @section 2 Mobile feed — urgency-sorted single column for phones.
// Stalled deals (overdue tasks) float to the top, then most recently active.
const ACQ_WARMTH: Record<string, { label: string; color: string }> = {
  stall:   { label: 'stalled', color: 'var(--neg)' },
  warm:    { label: 'active',  color: 'var(--pos)' },
  engaged: { label: 'recent',  color: 'var(--brand-4)' },
  cold:    { label: 'cold',    color: 'var(--tx-4)' },
};
function acqWarmthMeta(w: string) {
  return ACQ_WARMTH[w] ?? { label: 'aging', color: 'var(--warn)' };
}

type AcqFeedFilter = 'all' | 'stalled' | 'active';

function AcqMobileFeed({ deals, stageName }: { deals: Deal[]; stageName: (k: string) => string }) {
  const [filter, setFilter] = useState<AcqFeedFilter>('all');

  const touchTs = (d: Deal) => {
    const t = d.summary?.last_touched_at ?? d.updated_at;
    return t ? new Date(t).getTime() : 0;
  };

  const stalledCount = useMemo(() => deals.filter((d) => warmthClass(d) === 'stall').length, [deals]);
  const activeCount = useMemo(() => deals.filter((d) => ['warm', 'engaged'].includes(warmthClass(d))).length, [deals]);
  const askTotal = useMemo(() => deals.reduce((a, d) => a + (d.listing.asking_price ?? 0), 0), [deals]);

  const feed = useMemo(() => {
    const keep = (d: Deal) => {
      const w = warmthClass(d);
      return filter === 'all' ? true
        : filter === 'stalled' ? w === 'stall'
        : (w === 'warm' || w === 'engaged');
    };
    const rank = (d: Deal) => (warmthClass(d) === 'stall' ? 0 : 1);
    return deals.filter(keep).sort((a, b) => rank(a) - rank(b) || touchTs(b) - touchTs(a));
  }, [deals, filter]);

  const pills = [
    { key: 'all' as const,     label: 'All',     ct: deals.length },
    { key: 'stalled' as const, label: 'Stalled', ct: stalledCount },
    { key: 'active' as const,  label: 'Active',  ct: activeCount },
  ];

  return (
    <FeedScroll>
      <FeedHeader
        title="Acquisitions"
        summary={`${fmtUsd(askTotal)} asking · ${deals.length} deals${stalledCount > 0 ? ` · ${stalledCount} stalled` : ''}`}
      />
      <FeedPills options={pills} value={filter} onChange={setFilter} />
      {feed.length === 0 ? (
        <FeedEmpty>No deals in this view.</FeedEmpty>
      ) : (
        feed.map((d) => {
          const wm = acqWarmthMeta(warmthClass(d));
          const where = [d.listing.location_city, d.listing.location_state].filter(Boolean).join(', ');
          const meta = [d.listing.source_listing_id, where || null, d.broker?.company_name].filter(Boolean).join(' · ');
          return (
            <FeedCard
              key={d.deal_id}
              title={d.listing.business_name}
              value={fmtUsd(d.listing.asking_price)}
              chip={stageName(d.stage)}
              status={{ label: `${wm.label} · ${fmtDays(d.summary?.last_touched_at ?? d.updated_at)}`, color: wm.color }}
              meta={meta || undefined}
              to={`/acquisitions/${d.deal_id}`}
            />
          );
        })
      )}
    </FeedScroll>
  );
}

function DealCard({ d }: { d: Deal }) {
  const code = d.listing.source_listing_id ?? '—';
  const where = [d.listing.location_city, d.listing.location_state].filter(Boolean).join(', ');
  const ask = fmtUsd(d.listing.asking_price);
  const last = fmtDays(d.summary?.last_touched_at ?? d.updated_at);
  const warmth = warmthClass(d);
  return (
    <NavLink to={`/acquisitions/${d.deal_id}`} className={`acct-card ${warmth}`} style={{ textDecoration: 'none' }}>
      <div className="row1">
        <span className="name">{d.listing.business_name}</span>
        <span className="heat">{ask}</span>
      </div>
      <div className="meta">{code} · {where || 'no loc'}</div>
      <div className="foot">
        <span className="deal">{d.broker?.company_name ?? 'no broker'}</span>
        <span className="decay">
          <span className={`decay-bar ${warmth === 'stall' ? 'stale' : warmth === 'warm' ? 'fresh' : warmth === 'engaged' ? 'warm' : warmth === 'cold' ? 'cold' : 'aging'}`}><div /></span>
          {last}
        </span>
      </div>
    </NavLink>
  );
}
